import React, { useState } from 'react';
import { Info, Building2, Home } from 'lucide-react';
import { Modal } from './Common';

/**
 * Small info (i) button used in Project / Property view headers.
 * Opens a modal that explains what each entity is and when to use which.
 */
const EntityInfoButton = ({ size = 18 }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-label="What's the difference between a Project and a Property?"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          padding: 0,
          marginLeft: 8,
          borderRadius: '50%',
          border: '1px solid var(--gray-200)',
          background: 'var(--gray-50)',
          color: 'var(--gray-500)',
          cursor: 'pointer',
          verticalAlign: 'middle',
        }}
      >
        <Info size={size - 4} />
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Project vs Property" size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.6 }}>
            In this CRM, <strong>Project</strong> and <strong>Property</strong> are two distinct
            entities. Use the guide below to know when to use which.
          </p>

          {/* Project card */}
          <div style={{
            border: '1px solid var(--gray-200)',
            borderRadius: 10,
            padding: 14,
            background: 'var(--gray-50)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(29, 78, 216, 0.12)',
                color: '#1d4ed8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Building2 size={18} />
              </div>
              <strong style={{ fontSize: 14, color: 'var(--gray-900)' }}>Project</strong>
            </div>
            <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--gray-600)', lineHeight: 1.6 }}>
              An entire development, society, or complex. Multiple towers, total units, master
              amenities, RERA number, and possession date are all stored at the project level.
            </p>
            <div style={{ fontSize: 12, color: 'var(--gray-700)' }}>
              <strong>Add one when:</strong> you need to list a new society or development that
              contains multiple units.
            </div>
          </div>

          {/* Property card */}
          <div style={{
            border: '1px solid var(--gray-200)',
            borderRadius: 10,
            padding: 14,
            background: 'var(--gray-50)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(22, 163, 74, 0.12)',
                color: '#16a34a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Home size={18} />
              </div>
              <strong style={{ fontSize: 14, color: 'var(--gray-900)' }}>Property</strong>
            </div>
            <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--gray-600)', lineHeight: 1.6 }}>
              An individual unit — a flat, villa, or plot. It usually belongs to a Project. Unit-level
              details such as bedrooms, bathrooms, carpet area, floor, price, and owner contact are
              stored here.
            </p>
            <div style={{ fontSize: 12, color: 'var(--gray-700)' }}>
              <strong>Add one when:</strong> you need to list a specific flat, villa, or unit for
              sale or rent.
            </div>
          </div>

          {/* Relationship note */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '10px 12px',
            background: '#fef9e7',
            border: '1px solid #f1d57b',
            borderRadius: 8,
            fontSize: 12,
            color: '#8a6914',
            lineHeight: 1.5,
          }}>
            <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              <strong>Relationship:</strong> A single Project can contain multiple Properties
              (units). When adding a Property, you can link it to a parent Project.
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EntityInfoButton;
